const AWARE_INSTANT_PARTS = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Compare already-validated aware instants in PostgreSQL's microsecond domain.
 *
 * Public requests may carry offsets and more than six fractional digits while
 * frozen responses are exact UTC microseconds. Truncating (rather than
 * rounding) here keeps URL, request, and response cut ordering identical.
 */
export function compareInstantsAtMicrosecondPrecision(left: string, right: string): -1 | 0 | 1 {
  const micros = (value: string): bigint => {
    const match = AWARE_INSTANT_PARTS.exec(value);
    if (!match) throw new Error("compareInstantsAtMicrosecondPrecision requires validated aware timestamps");
    const wholeSecondMillis = Date.parse(`${match[1]}${match[3]}`);
    if (!Number.isFinite(wholeSecondMillis)) {
      throw new Error("compareInstantsAtMicrosecondPrecision requires validated calendar timestamps");
    }
    const fractionalMicros = BigInt(`${match[2] ?? ""}000000`.slice(0, 6));
    return BigInt(wholeSecondMillis) * 1_000n + fractionalMicros;
  };

  const leftMicros = micros(left);
  const rightMicros = micros(right);
  return leftMicros < rightMicros ? -1 : leftMicros > rightMicros ? 1 : 0;
}

/** Render an already-validated aware instant exactly as the backend's UTC-microsecond wire. */
export function normalizeInstantToUtcMicroseconds(value: string): string {
  const match = AWARE_INSTANT_PARTS.exec(value);
  if (!match) throw new Error("normalizeInstantToUtcMicroseconds requires a validated aware timestamp");
  const wholeSecondMillis = Date.parse(`${match[1]}${match[3]}`);
  if (!Number.isFinite(wholeSecondMillis)) {
    throw new Error("normalizeInstantToUtcMicroseconds requires a validated calendar timestamp");
  }
  const utcSecond = new Date(wholeSecondMillis).toISOString().slice(0, 19);
  const micros = `${match[2] ?? ""}000000`.slice(0, 6);
  return `${utcSecond}.${micros}Z`;
}
